import {
  useGetPortalLedger,
  useListPortalPaymentAttempts,
  useListPortalPayments,
  type LedgerRowOut,
  type PaymentOut,
  type PortalLeaseOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { okData, okItems } from '../../lib/apiData';
import { formatDate, formatMoney } from '../../lib/format';
import { METHOD_LABELS } from '../leases/labels';
import { NotLinked } from './NotLinked';
import { usePortalHome } from './usePortalHome';
import { TablePager, useTablePage } from '../../components/TablePager';

const LEDGER_COLUMNS: DataColumn<LedgerRowOut>[] = [
  { label: 'Date', render: (r) => formatDate(r.entry_date) },
  { label: 'Description', render: (r) => r.memo },
  {
    label: 'Charge',
    align: 'right',
    render: (r) => (Number(r.charge) ? formatMoney(r.charge) : ''),
  },
  {
    label: 'Payment',
    align: 'right',
    render: (r) => (Number(r.payment) ? formatMoney(r.payment) : ''),
  },
  { label: 'Balance', align: 'right', render: (r) => formatMoney(r.balance) },
];

const PAYMENT_COLUMNS: DataColumn<PaymentOut>[] = [
  { label: 'Received', render: (p) => formatDate(p.received_on) },
  { label: 'Rental', render: (p) => p.lease_label },
  { label: 'Amount', align: 'right', render: (p) => formatMoney(p.amount) },
  { label: 'Paid by', render: (p) => METHOD_LABELS[p.method] },
  {
    label: 'Status',
    render: (p) =>
      p.reversed_at ? (
        <Chip size="small" color="error" label="Reversed" />
      ) : (
        <Chip size="small" color="success" label="Received" />
      ),
  },
  {
    label: 'Receipt',
    align: 'right',
    render: (p) => (
      <Button size="small" href={`/api/portal/payments/${p.id}/receipt`}>
        Receipt
      </Button>
    ),
  },
];

function LeaseLedger({ lease, titled }: { lease: PortalLeaseOut; titled: boolean }) {
  const ledger = okData(useGetPortalLedger(lease.id).data);
  return (
    <Stack spacing={1}>
      <Typography variant="h6">{titled ? `Account: ${lease.label}` : 'Account history'}</Typography>
      {ledger?.rows.length === 0 ? (
        <Typography color="text.secondary">Nothing here yet.</Typography>
      ) : (
        <DataTable columns={LEDGER_COLUMNS} rows={ledger?.rows ?? []} rowKey={(r) => r.entry_id} />
      )}
    </Stack>
  );
}

export function PortalPaymentsPage() {
  const { home, notLinked } = usePortalHome();
  const paging = useTablePage();
  const paymentQuery = useListPortalPayments(paging.params, { query: { enabled: !!home } });
  const payments = okItems(paymentQuery.data);
  const attempts = okItems(useListPortalPaymentAttempts({ query: { enabled: !!home } }).data);

  if (notLinked) return <NotLinked />;
  if (!home) return null;
  return (
    <Stack spacing={3}>
      <Typography variant="h4">Payments</Typography>
      {attempts.map((a) =>
        a.status === 'processing' ? (
          <Alert key={a.id} severity="info">
            Your bank payment of {formatMoney(a.amount)} is on its way.
          </Alert>
        ) : (
          <Alert key={a.id} severity="warning">
            Your payment of {formatMoney(a.amount)} didn&apos;t go through.{' '}
            {a.failure_message ?? ''}
          </Alert>
        ),
      )}
      <Stack spacing={1}>
        <Typography variant="h6">Payments Received</Typography>
        {payments.length === 0 ? (
          <Typography color="text.secondary">No payments yet.</Typography>
        ) : (
          <>
            <DataTable columns={PAYMENT_COLUMNS} rows={payments} rowKey={(p) => p.id} />
            <TablePager paging={paging} total={okData(paymentQuery.data)?.total ?? 0} />
          </>
        )}
      </Stack>
      {home.leases.map((lease) => (
        <LeaseLedger key={lease.id} lease={lease} titled={home.leases.length > 1} />
      ))}
    </Stack>
  );
}
